import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { observeControlValue$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, type OnChanges, type OnInit, Output, type SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import type { BusinessIntelligenceScatterChartRepresentationConfig } from '../../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { canMeasuresBeSegmented } from '../../../../providers/business-intelligence/business-intelligence.utils';
import { BusinessIntelligenceRepresentationConfigEditorComponent } from '../business-intelligence-representation-config-editor.class';
import type { BusinessIntelligenceDimension, BusinessIntelligenceMeasure } from './../../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceDimensionConfiguration } from './../../../../interfaces/TailoredWidget.interface';
import { BusinessIntelligenceProvider } from './../../../../providers/business-intelligence/business-intelligence.provider';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-scatter-chart-representation-config-editor.scss'],
    selector: 'business-intelligence-scatter-chart-representation-config-editor',
    templateUrl: 'business-intelligence-scatter-chart-representation-config-editor.html',
    imports: [FormsModule, ReactiveFormsModule, UiFormFieldComponent, UiSelectComponent, AsyncPipe, TranslatePipe]
})
export class BusinessIntelligenceScatterChartRepresentationConfigEditorComponent
    extends BusinessIntelligenceRepresentationConfigEditorComponent<BusinessIntelligenceScatterChartRepresentationConfig>
    implements OnInit, OnChanges
{
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _fb = inject(UntypedFormBuilder);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligenceScatterChartRepresentationConfigEditorComponent>(
        this
    );

    @Input() config!: BusinessIntelligenceScatterChartRepresentationConfig;
    config$ = this._observableInputsManager.observe('config');

    @Input() availableMeasures: BusinessIntelligenceMeasure[] = [];
    availableMeasures$ = this._observableInputsManager.observe('availableMeasures');

    @Input() availableDimensions: BusinessIntelligenceDimension[] = [];
    availableDimensions$ = this._observableInputsManager.observe('availableDimensions');

    @Input() disabled!: boolean;

    @Output() configChanged: EventEmitter<BusinessIntelligenceScatterChartRepresentationConfig> = new EventEmitter();

    formGroup!: UntypedFormGroup;
    dimensions$!: Observable<BusinessIntelligenceDimension[]>;
    isMeasureSegmentable$!: Observable<boolean>;

    ngOnInit() {
        this.dimensions$ = this.availableDimensions$.pipe(
            filter(isNotNullish),
            map(dimensions => dimensions.filter(dimension => !this._businessIntelligenceProvider.isContinuousDimension(dimension)))
        );

        this.formGroup = this._fb.group({
            xAxisMeasureCode: [null, Validators.required],
            yAxisMeasureCode: [null, Validators.required],
            pointSizeMeasureCode: [null],
            groupByDimensionCode: [null, Validators.required],
            segmentationDimensionCode: [null]
        });

        this.isMeasureSegmentable$ = combineLatest([
            this.availableMeasures$.pipe(filter(isNotNullish)),
            observeControlValue$(this.formGroup.controls.xAxisMeasureCode),
            observeControlValue$(this.formGroup.controls.yAxisMeasureCode),
            observeControlValue$(this.formGroup.controls.pointSizeMeasureCode)
        ]).pipe(
            map(([availableMeasures, xAxisMeasureCode, yAxisMeasureCode, pointSizeMeasureCode]) =>
                canMeasuresBeSegmented(availableMeasures, [xAxisMeasureCode, yAxisMeasureCode, pointSizeMeasureCode])
            )
        );

        this.config$.pipe(untilDestroyed(this)).subscribe(config => {
            if (!config) {
                this.formGroup.setValue(
                    {
                        xAxisMeasureCode: null,
                        yAxisMeasureCode: null,
                        pointSizeMeasureCode: null,
                        groupByDimensionCode: null,
                        segmentationDimensionCode: null
                    },
                    { emitEvent: false }
                );
            } else {
                this.formGroup.setValue(
                    {
                        xAxisMeasureCode: config.xAxis ? config.xAxis.measureCode : null,
                        yAxisMeasureCode: config.yAxis ? config.yAxis.measureCode : null,
                        pointSizeMeasureCode: config.pointSize ? config.pointSize.measureCode : null,
                        groupByDimensionCode: config.groupBy ? config.groupBy.dimensionCode : null,
                        segmentationDimensionCode: config.segmentation ? config.segmentation.dimensionCode : null
                    },
                    { emitEvent: false }
                );
            }
        });

        this.formGroup.valueChanges
            .pipe(withLatestFrom(this.availableMeasures$.pipe(distinctUntilChanged())), untilDestroyed(this))
            .subscribe(([formValue, availableMeasures]) => {
                const isMeasuresBeSegmentable = canMeasuresBeSegmented(availableMeasures, [
                    formValue.xAxisMeasureCode,
                    formValue.yAxisMeasureCode,
                    formValue.pointSizeMeasureCode
                ]);
                const segmentationDimension: BusinessIntelligenceDimensionConfiguration =
                    formValue.segmentationDimensionCode && isMeasuresBeSegmentable
                        ? {
                              dimensionCode: formValue.segmentationDimensionCode
                          }
                        : null!;

                const groupByDimension: BusinessIntelligenceDimensionConfiguration = formValue.groupByDimensionCode
                    ? {
                          dimensionCode: formValue.groupByDimensionCode
                      }
                    : null!;

                this.configChanged.emit({
                    xAxis: {
                        measureCode: formValue.xAxisMeasureCode
                    },
                    yAxis: {
                        measureCode: formValue.yAxisMeasureCode
                    },
                    pointSize: formValue.pointSizeMeasureCode
                        ? {
                              measureCode: formValue.pointSizeMeasureCode
                          }
                        : null,
                    groupBy: groupByDimension,
                    segmentation: segmentationDimension,
                    click: null
                });
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }
}
